import React, { useEffect, useMemo, useState } from 'react';

import { Button, message, notification, Tooltip, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { postFiles } from '../helpers/api';
import {
  SERVICE_URI as FILE_SERVICE_URI,
  BASE_URI as FILE_BASE_URI,
} from '../services/files';

import { ALLOWED_FILE_ACCEPT, ALLOWED_FILE_TYPES } from '../helpers/constants';
import './FileUploader.scss';

const FileUploader = ({
  uploadUrl = `${FILE_SERVICE_URI}${FILE_BASE_URI}`,
  text = '',
  multiple = true,
  dragger = true,
  tooltip,
  accept = ALLOWED_FILE_ACCEPT,
  disabled,
  buttonType,
  onUploadSuccess = () => null,
  onUploadFailed,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState({});

  const defaultOnUploadFailed = (err) => {
    const messageCode = err?.inner?._;
    notification.error({
      message: messageCode
        ? t(messageCode)
        : 'A aparut o eroare, incercati din nou',
    });
  };

  const handleUploadFailed = onUploadFailed || defaultOnUploadFailed;

  useEffect(() => {
    const filesForUpload = Object.keys(files)
      .filter((uid) => !files[uid].uploaded)
      .map((uid) => files[uid].file);

    if (filesForUpload.length > 0) {
      setFiles(
        Object.keys(files).reduce(
          (acc, uid) => ({ ...acc, [uid]: { ...files[uid], uploaded: true } }),
          {},
        ),
      );
      postFiles(uploadUrl, filesForUpload)
        .then((res) => res.json())
        .then((res) => {
          onUploadSuccess(res, filesForUpload[0]);
        })
        .catch(handleUploadFailed);
    }
  }, [files, onUploadSuccess, uploadUrl, handleUploadFailed]);

  const props = useMemo(
    () => ({
      multiple,
      disabled,
      accept,
      showUploadList: false,
      customRequest: () => null,
      beforeUpload: (...[, filesBeforeUpload]) => {
        let hasInvalidFile = false;
        const convertBytesToMB = (bytes) => bytes / (1024 * 1024);
        const maxSize = process.env.MAX_FILE_SIZE || 5;
        filesBeforeUpload.forEach((file) => {
          if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            hasInvalidFile = true;
            message.error({
              content: t('errors.invalidFileType'),
              duration: 2.5,
              className: 'card-message',
            });
          } else if (convertBytesToMB(file.size) > maxSize) {
            hasInvalidFile = true;
            message.error({
              content: t('errors.maxFileSizeReached', {
                fileName: file.name,
                maxSize,
              }),
              duration: 2.5,
              className: 'card-message',
            });
          }
        });
        if (hasInvalidFile) {
          return;
        }

        setFiles((existingFiles) => ({
          ...existingFiles,
          ...filesBeforeUpload.reduce(
            (acc, cur) => ({
              ...acc,
              ...(cur.uid && !(cur.uid in existingFiles)
                ? { [cur.uid]: { file: cur, uploaded: false } }
                : null),
            }),
            {},
          ),
        }));
      },
    }),
    [accept, disabled, multiple, t],
  );

  return dragger ? (
    <div style={{ height: '80px' }}>
      <Upload.Dragger {...props}>
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">{text}</p>
      </Upload.Dragger>
    </div>
  ) : (
    <Tooltip title={tooltip}>
      <Upload {...props}>
        <Button type={buttonType || null} icon={<UploadOutlined />}>
          {text}
        </Button>
      </Upload>
    </Tooltip>
  );
};

export default FileUploader;
